import React, { useState, useEffect, useCallback } from 'react';
import { tasksAPI, aiAPI } from '../../../services/api';
import { useAuth } from '../../../context/AuthContext';
import { Button, Card, Modal, PriorityBadge, Paginator } from '../../../components/ui';
import { PRIORITIES } from '../../../utils/constants';
import toast from 'react-hot-toast';

const inp = { width: '100%', padding: '8px 12px', border: '1.5px solid #e2e8f0', borderRadius: 8, fontSize: 14, fontFamily: 'inherit', outline: 'none', background: '#fff', boxSizing: 'border-box' };
const sel = { ...inp, cursor: 'pointer' };

export default function TasksTab({ leadId }) {
  const { user } = useAuth();
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(false);
  const [form, setForm] = useState({ title: '', description: '', priority: 'Medium', due_date: '' });
  const [saving, setSaving] = useState(false);
  const [aiSuggestions, setAiSuggestions] = useState(null);
  const [aiLoading, setAiLoading] = useState(false);
  const [page, setPage] = useState(1);
  const PAGE_SIZE = 5;

  // Clamp page to valid range when tasks are added/removed
  useEffect(() => {
    const tp = Math.ceil(tasks.length / PAGE_SIZE);
    if (tp > 0 && page > tp) setPage(tp);
  }, [tasks.length]);

  const load = useCallback(async () => {
    try {
      const res = await tasksAPI.getByLead(leadId);
      setTasks(res.data?.data?.tasks || []);
    } catch {} finally { setLoading(false); }
  }, [leadId]);

  useEffect(() => { load(); }, [load]);

  const create = async () => {
    if (!form.title.trim()) { toast.error('Title required'); return; }
    setSaving(true);
    try {
      await tasksAPI.create({ ...form, lead_id: leadId, assigned_to: user?._id });
      toast.success('Task created');
      setModal(false);
      setForm({ title: '', description: '', priority: 'Medium', due_date: '' });
      load();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to create task');
    } finally { setSaving(false); }
  };

  const toggleComplete = async (task) => {
    const newStatus = task.status === 'Done' ? 'Pending' : 'Done';
    try {
      await tasksAPI.update(task._id, { status: newStatus });
      setTasks(ts => ts.map(t => t._id === task._id ? { ...t, status: newStatus } : t));
    } catch { toast.error('Failed to update task'); }
  };

  const isOverdue = (t) => t.status !== 'Done' && t.due_date && new Date(t.due_date) < new Date();

  const getAiSuggestions = async () => {
    setAiLoading(true);
    setAiSuggestions(null);
    try {
      const res = await aiAPI.tabAssist({ lead_id: leadId, tab: 'task' });
      setAiSuggestions(res.data?.data);
    } catch {
      toast.error('AI suggestion failed');
    } finally { setAiLoading(false); }
  };

  const createFromAi = async (suggestion) => {
    const dueDate = new Date();
    dueDate.setDate(dueDate.getDate() + (suggestion.due_days || 1));
    try {
      await tasksAPI.create({
        title: suggestion.title,
        description: suggestion.notes || '',
        priority: suggestion.priority || 'Medium',
        due_date: dueDate.toISOString().slice(0, 10),
        lead_id: leadId,
        assigned_to: user?._id
      });
      toast.success('Task created from AI suggestion');
      load();
    } catch { toast.error('Failed to create task'); }
  };

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
        <h3 style={{ fontSize: 15, fontWeight: 700, color: '#1B2A6B' }}>Tasks ({tasks.filter(t => t.status !== 'Done').length} open)</h3>
        <div style={{ display: 'flex', gap: 8 }}>
          <Button size="sm" variant="ghost" onClick={getAiSuggestions} disabled={aiLoading}>
            {aiLoading ? '⏳ Thinking…' : '✨ AI Suggest'}
          </Button>
          <Button size="sm" variant="primary" onClick={() => setModal(true)}>+ Add Task</Button>
        </div>
      </div>

      {/* AI Task Suggestions Panel */}
      {aiSuggestions && (
        <div style={{ background: '#f0f7ff', border: '1.5px solid #93c5fd', borderRadius: 12, padding: 16, marginBottom: 16 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
            <span style={{ fontSize: 13, fontWeight: 700, color: '#1e40af' }}>✨ AI Recommended Tasks</span>
            <button onClick={() => setAiSuggestions(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#94a3b8', fontSize: 16 }}>✕</button>
          </div>
          <p style={{ fontSize: 12, color: '#475569', marginBottom: 12, fontStyle: 'italic' }}>{aiSuggestions.reasoning}</p>
          {aiSuggestions.tasks?.map((s, i) => (
            <div key={i} style={{ background: '#fff', border: '1px solid #bfdbfe', borderRadius: 8, padding: '10px 12px', marginBottom: 8, display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 10 }}>
              <div style={{ flex: 1 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 3 }}>
                  <span style={{ fontSize: 13, fontWeight: 600, color: '#334155' }}>{s.title}</span>
                  <span style={{ fontSize: 11, padding: '1px 7px', borderRadius: 10, fontWeight: 600, background: s.priority === 'High' ? '#fee2e2' : s.priority === 'Low' ? '#f0fdf4' : '#fef3c7', color: s.priority === 'High' ? '#dc2626' : s.priority === 'Low' ? '#16a34a' : '#d97706' }}>{s.priority}</span>
                  <span style={{ fontSize: 11, color: '#94a3b8' }}>Due in {s.due_days}d</span>
                </div>
                {s.notes && <p style={{ fontSize: 12, color: '#64748b' }}>{s.notes}</p>}
              </div>
              <Button size="sm" variant="primary" onClick={() => createFromAi(s)}>+ Add</Button>
            </div>
          ))}
        </div>
      )}

      {loading ? <p style={{ color: '#94a3b8', fontSize: 13 }}>Loading…</p> : tasks.length === 0 ? (
        <p style={{ color: '#94a3b8', fontSize: 13, textAlign: 'center', padding: 24 }}>No tasks yet</p>
      ) : tasks.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE).map(task => (
        <Card key={task._id} style={{ marginBottom: 10, padding: '10px 14px', border: isOverdue(task) ? '1.5px solid #fca5a5' : '1px solid #e2e8f0', opacity: task.status === 'Done' ? 0.65 : 1 }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
            <input type="checkbox" checked={task.status === 'Done'} onChange={() => toggleComplete(task)}
              style={{ marginTop: 2, cursor: 'pointer', width: 15, height: 15, flexShrink: 0 }} />
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 2 }}>
                <p style={{ fontSize: 13, fontWeight: 600, color: '#334155', textDecoration: task.status === 'Done' ? 'line-through' : 'none' }}>{task.title}</p>
                <PriorityBadge priority={task.priority} />
                {isOverdue(task) && <span style={{ fontSize: 10, background: '#fee2e2', color: '#dc2626', padding: '1px 5px', borderRadius: 4, fontWeight: 700 }}>OVERDUE</span>}
              </div>
              {task.description && <p style={{ fontSize: 12, color: '#64748b' }}>{task.description}</p>}
              {task.due_date && <p style={{ fontSize: 11, color: isOverdue(task) ? '#dc2626' : '#94a3b8' }}>Due: {new Date(task.due_date).toLocaleDateString('en-IN')}</p>}
            </div>
          </div>
        </Card>
      ))}

      <Paginator page={page} totalPages={Math.ceil(tasks.length / PAGE_SIZE)} onPageChange={setPage} />

      <Modal open={modal} onClose={() => setModal(false)} title="Add Task">
        <div style={{ display: 'grid', gap: 14 }}>
          <div>
            <label style={{ fontSize: 12, fontWeight: 600, color: '#475569', display: 'block', marginBottom: 4 }}>Title *</label>
            <input value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} style={inp} placeholder="Task title" />
          </div>
          <div>
            <label style={{ fontSize: 12, fontWeight: 600, color: '#475569', display: 'block', marginBottom: 4 }}>Description</label>
            <textarea value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} style={{ ...inp, minHeight: 60, resize: 'vertical' }} />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div>
              <label style={{ fontSize: 12, fontWeight: 600, color: '#475569', display: 'block', marginBottom: 4 }}>Priority</label>
              <select value={form.priority} onChange={e => setForm(f => ({ ...f, priority: e.target.value }))} style={sel}>
                {PRIORITIES.map(p => <option key={p} value={p}>{p}</option>)}
              </select>
            </div>
            <div>
              <label style={{ fontSize: 12, fontWeight: 600, color: '#475569', display: 'block', marginBottom: 4 }}>Due Date</label>
              <input type="date" value={form.due_date} onChange={e => setForm(f => ({ ...f, due_date: e.target.value }))} style={inp} />
            </div>
          </div>
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
            <Button variant="ghost" onClick={() => setModal(false)}>Cancel</Button>
            <Button variant="primary" onClick={create} disabled={saving}>{saving ? 'Saving…' : 'Create Task'}</Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
